import type { NumberFormatter } from '@/lib/number-format'
import { OrderSide, OrderStatus } from '@/types/generated/common/trading'
import type { UnifiedOrderItem } from '@/types/generated/public_api'

export interface ExecutionMarker {
  id: string
  bucketTime: number
  executionTime: number
  isBuy: boolean
  quantity: string
  price: string
}

interface RemovableTradingPrimitive {
  remove(): void
}

/**
 * TradingView creates line primitives before their appearance setters run. If one of
 * those setters throws, remove the partially configured primitive so its default
 * `position 0` UI cannot remain on the chart.
 */
export function configureTradingPrimitive<T extends RemovableTradingPrimitive>(
  primitive: T,
  configure: (primitive: T) => void
): T | null {
  try {
    configure(primitive)
    return primitive
  } catch {
    try {
      primitive.remove()
    } catch {}
    return null
  }
}

/** Own the whole batch until every primitive is created and configured. */
export async function createTradingPrimitiveBatch<T extends RemovableTradingPrimitive>(
  creators: (() => Promise<T>)[],
  configure: (primitive: T, index: number) => void,
  isCancelled: () => boolean
): Promise<T[] | null> {
  // Wait for late successes too: Promise.all would lose them after the first rejection.
  const results = await Promise.allSettled(creators.map((create) => Promise.resolve().then(create)))
  const primitives = results.flatMap((result) =>
    result.status === 'fulfilled' ? [result.value] : []
  )
  let committed = false
  try {
    if (primitives.length !== creators.length || isCancelled()) return null
    primitives.forEach(configure)
    if (isCancelled()) return null
    committed = true
    return primitives
  } catch {
    return null
  } finally {
    if (!committed) {
      for (const primitive of primitives) {
        try {
          primitive.remove()
        } catch {}
      }
    }
  }
}

const DAY_SECONDS = 86_400
const WEEK_SECONDS = 7 * DAY_SECONDS
const FIRST_UNIX_MONDAY_SECONDS = 4 * DAY_SECONDS
const MAX_EXECUTION_MARKS_PER_BAR = 10

function normalizeExecutionTimeframe(timeframeSeconds: number): number {
  return Number.isFinite(timeframeSeconds) && timeframeSeconds > 0 ? timeframeSeconds : 60
}

export function getExecutionBucketTime(
  timestamp: number,
  timeframeSeconds: number,
  sortedBarTimes: number[] = []
): number {
  const bucketSize = normalizeExecutionTimeframe(timeframeSeconds)
  let low = 0
  let high = sortedBarTimes.length - 1
  let matchingBarTime: number | undefined
  while (low <= high) {
    const middle = Math.floor((low + high) / 2)
    const barTime = sortedBarTimes[middle]
    if (barTime <= timestamp) {
      matchingBarTime = barTime
      low = middle + 1
    } else {
      high = middle - 1
    }
  }
  if (matchingBarTime !== undefined && timestamp < matchingBarTime + bucketSize) {
    return matchingBarTime
  }
  if (bucketSize === WEEK_SECONDS) {
    return (
      Math.floor((timestamp - FIRST_UNIX_MONDAY_SECONDS) / WEEK_SECONDS) * WEEK_SECONDS +
      FIRST_UNIX_MONDAY_SECONDS
    )
  }
  return Math.floor(timestamp / bucketSize) * bucketSize
}

export function filterExecutionMarkersToVisibleRange(
  markers: ExecutionMarker[],
  visibleRange: { from: number; to: number },
  timeframeSeconds: number
): ExecutionMarker[] {
  const bucketSize = normalizeExecutionTimeframe(timeframeSeconds)
  const visibleFrom = getExecutionBucketTime(visibleRange.from, bucketSize)
  const visibleTo = getExecutionBucketTime(visibleRange.to, bucketSize)

  return markers.filter(({ bucketTime }) => bucketTime >= visibleFrom && bucketTime <= visibleTo)
}

export function buildExecutionMarkers(
  trades: UnifiedOrderItem[],
  options: {
    accountId?: string
    accountIds?: string[]
    symbol: string
    timeframeSeconds: number
    barTimes?: number[]
  }
): ExecutionMarker[] {
  const accountIds = options.accountIds ?? (options.accountId ? [options.accountId] : [])
  if (accountIds.length === 0) return []
  const accountIdSet = new Set(accountIds)

  const bucketSize = normalizeExecutionTimeframe(options.timeframeSeconds)
  const sortedBarTimes = options.barTimes?.slice().sort((a, b) => a - b) ?? []
  const markers: ExecutionMarker[] = []

  for (const item of dedupeTradesByAccountAndOrderId(trades)) {
    const order = item.order
    if (
      !order ||
      !accountIdSet.has(order.accountId) ||
      order.symbol !== options.symbol ||
      (order.status !== OrderStatus.ORDER_STATUS_FILLED &&
        !(
          order.status === OrderStatus.ORDER_STATUS_PARTIALLY_FILLED &&
          Number(order.filledQuantity) > 0
        ))
    ) {
      continue
    }

    if (order.side !== OrderSide.ORDER_SIDE_BUY && order.side !== OrderSide.ORDER_SIDE_SELL) {
      continue
    }

    const timestamp = Number(order.updatedAt || order.createdAt) / 1000
    if (!Number.isFinite(timestamp) || timestamp <= 0) continue

    const priceValue = Number(order.avgFillPrice)
    const fallbackPriceValue = Number(order.price)
    const price =
      Number.isFinite(priceValue) && priceValue > 0
        ? order.avgFillPrice!
        : Number.isFinite(fallbackPriceValue) && fallbackPriceValue > 0
          ? order.price!
          : undefined
    const filledQuantityValue = Number(order.filledQuantity)
    const quantityValue = Number(order.quantity)
    const quantity =
      Number.isFinite(filledQuantityValue) && filledQuantityValue > 0
        ? order.filledQuantity!
        : Number.isFinite(quantityValue) && quantityValue > 0
          ? order.quantity
          : undefined
    if (!price || !quantity) continue

    const bucketTime = getExecutionBucketTime(timestamp, bucketSize, sortedBarTimes)
    const isBuy = order.side === OrderSide.ORDER_SIDE_BUY
    markers.push({
      id: `${order.accountId}:${order.orderId}:${order.venueSymbol || timestamp}`,
      bucketTime,
      executionTime: timestamp,
      isBuy,
      quantity,
      price,
    })
  }

  const countByBucket = new Map<number, number>()
  return markers
    .sort((a, b) => a.bucketTime - b.bucketTime || a.executionTime - b.executionTime)
    .filter(({ bucketTime }) => {
      const count = countByBucket.get(bucketTime) ?? 0
      if (count >= MAX_EXECUTION_MARKS_PER_BAR) return false
      countByBucket.set(bucketTime, count + 1)
      return true
    })
}

function formatExecutionNumber(value: string): string {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 12 })
}

/** Pass `fmt` to localize separators and mask the (account-level) fill quantity while
 *  balances are hidden; the fill price is market data and stays visible. */
export function formatExecutionMarkerTooltip(
  marker: ExecutionMarker,
  fmt?: NumberFormatter
): string {
  const quantity = formatExecutionNumber(marker.quantity)
  const price = formatExecutionNumber(marker.price)
  return `${marker.isBuy ? 'Buy' : 'Sell'}: ${
    fmt ? fmt.text(quantity, { sensitive: true }) : quantity
  } at ${fmt ? fmt.text(price) : price}`
}

export function getPositionOverlayKey(row: {
  accountId: string
  exchange: number
  symbol: string
}): string {
  return `${row.accountId}:${row.exchange}:${row.symbol}`
}

export function dedupeTradesByAccountAndOrderId(trades: UnifiedOrderItem[]): UnifiedOrderItem[] {
  const seen = new Set<string>()
  const result: UnifiedOrderItem[] = []

  for (const item of trades) {
    const order = item.order
    if (!order?.orderId) continue
    const key = `${order.accountId}:${order.orderId}:${order.venueSymbol || ''}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }

  return result
}

export function fallbackAccountLabel(accountId: string): string {
  if (!accountId) return 'Account'
  return accountId.length > 8 ? `${accountId.slice(0, 6)}…` : accountId
}
